import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { search, buildSnippet } from "../search";

const PAGE_SIZE = 20;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [state, setState] = useState({ kind: "idle" });
  const [page, setPage] = useState(0);
  const [filterType, setFilterType] = useState("all"); // all | verse | note
  const [filterTestament, setFilterTestament] = useState("both");

  useEffect(() => {
    if (!q.trim()) {
      setState({ kind: "idle" });
      return;
    }
    let stale = false;
    setPage(0);
    setState({ kind: "loading" });
    search(q, {
      testament: filterTestament,
      type: filterType === "all" ? undefined : filterType,
    })
      .then((r) => {
        if (stale) return;
        if (r.kind === "nav") {
          window.location.assign(r.target);
          return;
        }
        setState(r);
      })
      .catch((err) => {
        if (!stale) setState({ kind: "error", err });
      });
    return () => {
      stale = true;
    };
  }, [q, filterType, filterTestament]);

  const pagedHits = useMemo(() => {
    if (state.kind !== "results") return [];
    return state.hits.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  }, [state, page]);

  const totalPages =
    state.kind === "results"
      ? Math.max(1, Math.ceil(state.hits.length / PAGE_SIZE))
      : 1;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="search-page">
      <div
        className="parchment-card p-7 md:p-9 fade-in"
        data-testid="search-header-card"
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <Link
            to="/"
            className="btn-ghost"
            data-testid="search-back"
            style={{ fontSize: "0.78rem" }}
          >
            <ArrowLeft size={14} /> INDEX
          </Link>
          <div
            className="commentary-verse-label"
            style={{ letterSpacing: "0.22em" }}
          >
            SCRIPTORIUM · SEARCH
          </div>
        </div>

        <SearchForm
          initial={q}
          onSubmit={(next) => setParams({ q: next })}
        />

        <div
          className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2"
          style={{
            fontSize: "0.9rem",
            color: "var(--ink-muted)",
            fontStyle: "italic",
          }}
          data-testid="search-filters"
        >
          <Pill
            label="Both Testaments"
            active={filterTestament === "both"}
            onClick={() => setFilterTestament("both")}
          />
          <Pill
            label="New only"
            active={filterTestament === "nt"}
            onClick={() => setFilterTestament("nt")}
          />
          <Pill
            label="Old only"
            active={filterTestament === "ot"}
            onClick={() => setFilterTestament("ot")}
          />
          <span style={{ color: "var(--rule)" }}>·</span>
          <Pill
            label="All"
            active={filterType === "all"}
            onClick={() => setFilterType("all")}
          />
          <Pill
            label="Verses"
            active={filterType === "verse"}
            onClick={() => setFilterType("verse")}
          />
          <Pill
            label="Commentary"
            active={filterType === "note"}
            onClick={() => setFilterType("note")}
          />
        </div>
      </div>

      <div className="mt-8">
        {state.kind === "idle" && <Idle />}
        {state.kind === "loading" && <Loading />}
        {state.kind === "error" && <ErrorCard err={state.err} />}
        {state.kind === "results" && (
          <>
            <div
              className="mb-5 text-center"
              style={{
                fontFamily: "'IM Fell English', serif",
                fontStyle: "italic",
                color: "var(--ink-soft)",
                fontSize: "1.05rem",
              }}
              data-testid="search-summary"
            >
              {state.total === 0
                ? `No matching scripture or commentary for "${q}".`
                : `${state.total.toLocaleString()} result${state.total === 1 ? "" : "s"} for "${q}"`}
            </div>

            <div className="space-y-4">
              {pagedHits.map((hit) => (
                <ResultCard key={hit.id} hit={hit} query={q} />
              ))}
            </div>

            {totalPages > 1 && (
              <div
                className="mt-8 flex items-center justify-center gap-3"
                data-testid="search-pager"
              >
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="search-prev"
                >
                  ‹ PREV
                </button>
                <span
                  style={{
                    fontFamily: "'IM Fell English SC', serif",
                    color: "var(--ink-muted)",
                  }}
                >
                  page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={page === totalPages - 1}
                  data-testid="search-next"
                >
                  NEXT ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SearchForm({ initial, onSubmit }) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = val.trim();
        if (v) onSubmit(v);
      }}
      data-testid="search-form"
    >
      <input
        type="search"
        inputMode="search"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck="false"
        enterKeyHint="search"
        className="parchment-input"
        placeholder="Augustine · Eucharist · Melchisedech · Matt 5:8…"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid="search-input"
        style={{ padding: "0.75rem 0.95rem", fontSize: "1.1rem" }}
        autoFocus
      />
    </form>
  );
}

function Pill({ label, active, onClick }) {
  return (
    <button
      type="button"
      className="btn-ghost"
      onClick={onClick}
      data-testid={`search-pill-${label.replace(/\s+/g, "-").toLowerCase()}`}
      style={
        active
          ? {
              background: "var(--oxblood)",
              color: "var(--parchment-light)",
              borderColor: "var(--oxblood)",
              fontSize: "0.76rem",
            }
          : { fontSize: "0.76rem" }
      }
    >
      {label}
    </button>
  );
}

function Idle() {
  return (
    <div
      className="parchment-card p-8 text-center fade-in"
      data-testid="search-idle"
    >
      <p
        style={{
          fontFamily: "'IM Fell English', serif",
          fontStyle: "italic",
          color: "var(--ink-soft)",
          fontSize: "1.05rem",
          margin: 0,
        }}
      >
        Type a name, a topic, or a verse reference above.
      </p>
      <p
        style={{
          color: "var(--ink-muted)",
          fontSize: "0.9rem",
          marginTop: "0.6rem",
          fontStyle: "italic",
        }}
      >
        Examples: <em>Augustine</em>, <em>Eucharist</em>, <em>grace</em>,
        <em> Matt 5:8</em>, <em>Melchisedech</em>.
      </p>
    </div>
  );
}

function Loading() {
  return (
    <div
      className="text-center fade-in"
      data-testid="search-loading"
      style={{
        fontFamily: "'IM Fell English', serif",
        fontStyle: "italic",
        color: "var(--ink-soft)",
      }}
    >
      ❦ Loading scholarly index…
    </div>
  );
}

function ErrorCard({ err }) {
  return (
    <div
      className="parchment-card p-7 text-center fade-in"
      data-testid="search-error"
      style={{ color: "var(--oxblood-dark)" }}
    >
      <p style={{ margin: 0 }}>
        Search index could not be loaded. ({err?.message ?? "unknown error"})
      </p>
    </div>
  );
}

function ResultCard({ hit, query }) {
  const href = `/book/${hit.bookSlug}/chapter/${hit.chapter}#v=${hit.verse}`;
  return (
    <Link
      to={href}
      className="parchment-card block p-6 fade-in"
      data-testid={`search-card-${hit.id}`}
      style={{ textDecoration: "none" }}
    >
      <div className="commentary-verse-label" style={{ marginBottom: "0.5rem" }}>
        {hit.book.toUpperCase()} · {hit.chapter}:{hit.verse}
        {hit.type === "note" && hit.attribution && (
          <span className="attribution">
            {" "}— note by {hit.attribution}
          </span>
        )}
      </div>
      <p
        className="commentary-note"
        style={{ margin: 0 }}
        dangerouslySetInnerHTML={{
          __html: buildSnippet(hit.text, query),
        }}
      />
    </Link>
  );
}
